import { useNavigate } from "@tanstack/react-router";
import { Edit, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DatePickerInput } from "@/components/shared/form/date-picker-input";
import { TextInput } from "@/components/shared/form/text-input";
import { InvoiceSource } from "@/components/shared/miscellaneous/invoice-source";
import { Button } from "@/components/ui/button";
import {
  PopoverForm,
  PopoverFormContent,
  PopoverFormTrigger,
} from "@/components/ui/popover-form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TypographyMuted, TypographySmall } from "@/components/ui/typography";
import { useTenant } from "@/providers/session";
import { useUpdateInvoice } from "@/queries/invoices/update";
import { z } from "zod";

type Props = {
  invoiceId: string;
  createdAt: Date;
  invoiceNo: string;
  dueDate: Date | null;
  source: string;
  onDelete: () => void;
  disabled?: React.ReactNode;
};

export const InvoiceBanner = ({
  invoiceId,
  createdAt,
  source,
  dueDate,
  invoiceNo,
  onDelete,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const [updateInvoice] = useUpdateInvoice();

  const formattedDate = createdAt.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const formattedTime = createdAt.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const formattedDueDate = dueDate
    ? dueDate.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

  return (
    <div className="flex flex-row bg-slate-50 p-2 rounded-md items-center gap-4">
      <div className="flex-1 flex flex-row gap-x-4 flex-wrap items-center">
        <div className="flex flex-row items-center gap-2">
          <TypographyMuted>{`${t("created_at")}:`}</TypographyMuted>
          <TypographySmall className="text-primary/90">
            {formattedDate} {formattedTime}
          </TypographySmall>
        </div>
        <div className="flex flex-row items-center gap-2">
          <TypographyMuted>{`${t("source")}:`}</TypographyMuted>
          <InvoiceSource source={source} />
        </div>
        <div className="flex flex-row items-center gap-2">
          <TypographyMuted>{`${t("due_date")}:`}</TypographyMuted>
          <PopoverForm>
            <div className="relative">
              <PopoverFormTrigger asChild>
                <Button
                  variant="ghost"
                  className="p-0 h-auto hover:bg-transparent"
                >
                  <TypographySmall className="text-primary/90">
                    {dueDate?.getTime() === 0 ? "-" : formattedDueDate}
                  </TypographySmall>
                  <Edit className="w-4 h-4 ml-1" />
                </Button>
              </PopoverFormTrigger>
            </div>
            <PopoverFormContent<typeof dueDateSchema>
              onSubmit={handleDueDateSubmit}
              schema={dueDateSchema}
              initialValues={{
                dueDate: dueDate?.getTime() === 0 ? new Date() : dueDate,
              }}
            >
              <DatePickerInput name="dueDate" />
            </PopoverFormContent>
          </PopoverForm>
        </div>
        <div className="flex flex-row items-center gap-2">
          <TypographyMuted>{`${t("invoice_no")}:`}</TypographyMuted>
          <PopoverForm>
            <div className="relative">
              <PopoverFormTrigger asChild>
                <Button
                  variant="ghost"
                  className="p-0 h-auto hover:bg-transparent disabled:cursor-not-allowed disabled:opacity-100"
                  disabled={disabled}
                >
                  <TypographySmall className="text-primary/90">
                    {invoiceNo ? invoiceNo : "-"}
                  </TypographySmall>
                  <Edit className="w-4 h-4 ml-1" />
                </Button>
              </PopoverFormTrigger>
            </div>
            <PopoverFormContent<typeof invoiceNoSchema>
              onSubmit={handleInvoiceNumberSubmit}
              schema={invoiceNoSchema}
              initialValues={{ invoiceNo: invoiceNo }}
            >
              <TextInput name="invoiceNo" />
            </PopoverFormContent>
          </PopoverForm>
        </div>
      </div>
      <div className="flex flex-row gap-1">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDelete}
                className="gap-1"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("delete")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                className="gap-1 border-none bg-transparent"
              >
                <X className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="capitalize">{t("close")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );

  function handleClose() {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate({
        to: "/$company/unternehmen",
        params: { company: companySlug },
        replace: true,
      });
    }
  }

  async function handleDueDateSubmit({ dueDate }: { dueDate: Date | null }) {
    await updateInvoice({ invoiceId, dueDate });
  }

  async function handleInvoiceNumberSubmit({
    invoiceNo,
  }: {
    invoiceNo: string | null;
  }) {
    await updateInvoice({ invoiceId, invoiceNo });
  }

  function handleDelete() {
    onDelete();
  }
};

const invoiceNoSchema = z.object({
  invoiceNo: z.string().min(1).max(255).nullable(),
});

const dueDateSchema = z.object({
  dueDate: z.date().nullable(),
});
